import { z } from 'zod';

import { backofficeValidators } from '@/backoffice/redux/api/_generated/validators/backoffice-validators.generated';
import { billingBackofficeValidators } from '@/backoffice/redux/api/_generated/validators/billing-backoffice-validators.generated';
import { ideaasBackofficeValidators } from '@/backoffice/redux/api/_generated/validators/ideaas-backoffice-validators.generated';
import { assetsValidators } from '@/shared/redux/api/_generated/validators/assets-validators.generated';
import { auditValidators } from '@/shared/redux/api/_generated/validators/audit-validators.generated';
import { billingValidators } from '@/shared/redux/api/_generated/validators/billing-validators.generated';
import { ideaasV2Validators } from '@/shared/redux/api/_generated/validators/ideaas-v2-validators.generated';
import { ideaasValidators } from '@/shared/redux/api/_generated/validators/ideaas-validators.generated';
import { identPublicValidators } from '@/shared/redux/api/_generated/validators/ident-public-validators.generated';
import { identValidators } from '@/shared/redux/api/_generated/validators/ident-validators.generated';
import { integrationValidators } from '@/shared/redux/api/_generated/validators/integration-validators.generated';
import { jobsValidators } from '@/shared/redux/api/_generated/validators/jobs-validators.generated';
import { notificationsValidators } from '@/shared/redux/api/_generated/validators/notifications-validators.generated';
import { offboardingValidators } from '@/shared/redux/api/_generated/validators/offboarding-validators.generated';
import { openplcValidators } from '@/shared/redux/api/_generated/validators/openplc-validators.generated';
import { sdaiValidators } from '@/shared/redux/api/_generated/validators/sdai-validators.generated';
import { vscodeValidators } from '@/shared/redux/api/_generated/validators/vscode-validators.generated';

// Merge per-service validator maps into one registry. Fails loud at module
// load if two services declare an operation with the same name — silently
// picking the later entry would misclassify the loser's correct responses
// as schema drift. Catching this here (rather than at codegen time) keeps
// the per-service generators independent; CI exercises this module via
// the operation-validators tests, so a collision blocks the merge.
export const mergeValidatorMaps = (
  maps: readonly Record<string, z.ZodTypeAny>[],
): Record<string, z.ZodTypeAny> => {
  const merged: Record<string, z.ZodTypeAny> = {};
  const collisions = new Set<string>();
  for (const map of maps) {
    for (const [key, value] of Object.entries(map)) {
      if (key in merged) collisions.add(key);
      merged[key] = value;
    }
  }
  if (collisions.size > 0) {
    throw new Error(
      `Operation name collisions across GraphQL validator registries: ${[
        ...collisions,
      ].join(
        ', ',
      )}. Rename one of the operations so each name resolves to a single schema.`,
    );
  }
  return merged;
};

const operationValidators = mergeValidatorMaps([
  {
    // Dynamic alias query outside codegen; keep in sync with the singular tag shape.
    ReadLinkedTagsForAssets: z.record(
      z.object({
        items: z.array(
          z
            .object({
              name: z.string(),
            })
            .nullish(),
        ),
      }),
    ),
  },
  assetsValidators,
  auditValidators,
  billingValidators,
  ideaasValidators,
  ideaasV2Validators,
  identValidators,
  identPublicValidators,
  integrationValidators,
  jobsValidators,
  notificationsValidators,
  offboardingValidators,
  openplcValidators,
  sdaiValidators,
  vscodeValidators,
  backofficeValidators,
  billingBackofficeValidators,
  ideaasBackofficeValidators,
]);

export const registerValidatorMap = (map: Record<string, z.ZodTypeAny>) => {
  const added = Object.fromEntries(
    Object.entries(map).filter(
      ([name, schema]) => operationValidators[name] !== schema,
    ),
  );

  Object.assign(
    operationValidators,
    mergeValidatorMaps([operationValidators, added]),
  );
};

export const getOperationValidator = (
  operationName: string | undefined,
): z.ZodTypeAny | undefined =>
  operationName ? operationValidators[operationName] : undefined;

// Match the operation name from a GraphQL document string. Codegen emits
// `query OpName(...)` / `mutation OpName(...)` / `subscription OpName(...)`
// as the first meaningful token of every document, so a regex is enough —
// we avoid parsing the full AST on every request.
//
// Anchored to the start of the string (with optional leading whitespace
// and `#` line comments) so we never pick up an operation keyword that
// appears inside a string literal, argument list, or trailing fragment
// definition — those have produced the wrong name in unanchored regexes
// elsewhere.
const OPERATION_NAME_REGEX =
  /^(?:\s|#[^\n]*\n)*(?:query|mutation|subscription)\s+(\w+)/;

export const extractOperationName = (
  document: string | undefined,
): string | undefined => {
  if (!document) return undefined;
  const match = OPERATION_NAME_REGEX.exec(document);
  return match?.[1];
};
